"use client";

import {
  Loader2Icon,
  SendIcon,
  XIcon,
  CheckIcon,
  UserIcon,
  MailIcon,
  MessageSquareIcon,
  PhoneIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";

import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ShimmeringText } from "./ui/shimmering-text";
import { cn } from "../lib/utils";
import { AudioRecorderComponent } from "./audio-recorder";
import {
  VoiceFeedbackWidgetProps,
  FeedbackSubmissionResponse,
  FormField,
} from "../types";

// Helper function to create dynamic schema
const createFeedbackSchema = (
  showFields: FormField[],
  required: boolean,
  defaultValues?: { name?: string; email?: string; phone?: string }
) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  if (showFields.includes("name")) {
    if (required) {
      schemaFields.name = z
        .string()
        .min(1, "Name is required")
        .max(100, "Name must be less than 100 characters");
    } else {
      schemaFields.name = z
        .string()
        .max(100, "Name must be less than 100 characters")
        .optional()
        .or(z.literal(""));
    }
  }

  if (showFields.includes("email")) {
    if (required) {
      schemaFields.email = z
        .string()
        .min(1, "Email is required")
        .email("Please enter a valid email address");
    } else {
      schemaFields.email = z
        .string()
        .email("Please enter a valid email address")
        .optional()
        .or(z.literal(""));
    }
  }

  if (showFields.includes("phone")) {
    if (required) {
      schemaFields.phone = z
        .string()
        .min(1, "Phone is required")
        .max(50, "Phone must be less than 50 characters");
    } else {
      schemaFields.phone = z
        .string()
        .max(50, "Phone must be less than 50 characters")
        .optional()
        .or(z.literal(""));
    }
  }

  // Message is always present but optional if audio is provided
  schemaFields.message = z
    .string()
    .max(1000, "Message must be less than 1000 characters")
    .optional()
    .or(z.literal(""));

  return z.object(schemaFields);
};

type FeedbackFormData = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
};

/**
 * Generic API request handler with timeout support and error handling
 */
async function apiRequest<T>(
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<T> {
  const { timeout = 30000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `API request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new Error("Request timeout");
      }
      throw error;
    }

    throw new Error("Unknown error occurred");
  }
}

export function VoiceFeedbackWidget({
  className,
  options: {
    apiEndpoint = "https://voice-feedback-agent.vercel.app/api/feedback",
    onSuccess,
    onError,
    title = "Feedback Form",
    description = "Share your feedback with us",
    defaultMinimized = false,
    maxRecordingDuration = 90,
    form,
  },
}: {
  className: string;
  options: VoiceFeedbackWidgetProps;
}) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const [isAnimating, setIsAnimating] = useState(false);

  // Get form configuration
  const showFields = form?.show || ["name", "email"];
  const isRequired = form?.required !== false; // Default to true
  const defaultValues = form?.defaultValues || {};

  // Create dynamic schema based on visible fields
  const feedbackSchema = createFeedbackSchema(
    showFields,
    isRequired,
    defaultValues
  );

  // Build default values for react-hook-form
  const formDefaultValues: FeedbackFormData = {
    message: "",
  };

  // Include visible fields with their default values or empty strings
  if (showFields.includes("name")) {
    formDefaultValues.name = defaultValues.name || "";
  }
  if (showFields.includes("email")) {
    formDefaultValues.email = defaultValues.email || "";
  }
  if (showFields.includes("phone")) {
    formDefaultValues.phone = defaultValues.phone || "";
  }

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    mode: "onChange",
    defaultValues: formDefaultValues,
  });

  const watchedMessage = watch("message");

  // Custom validation for audio requirement
  const isFormValid = isValid && (watchedMessage?.trim() || audioBlob);

  const { mutateAsync: onSubmit, isPending: isSubmitting } = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      const formDataToSend = new FormData();

      // Include all default values (even if hidden)
      if (defaultValues.name) {
        formDataToSend.append("name", defaultValues.name);
      }
      if (defaultValues.email) {
        formDataToSend.append("email", defaultValues.email);
      }
      if (defaultValues.phone) {
        formDataToSend.append("phone", defaultValues.phone);
      }

      // Override with form data for visible fields
      if (showFields.includes("name") && data.name?.trim()) {
        formDataToSend.set("name", data.name.trim());
      }
      if (showFields.includes("email") && data.email?.trim()) {
        formDataToSend.set("email", data.email.trim());
      }
      if (showFields.includes("phone") && data.phone?.trim()) {
        formDataToSend.set("phone", data.phone.trim());
      }

      if (data.message?.trim()) {
        formDataToSend.append("message", data.message.trim());
      }

      if (audioBlob) {
        formDataToSend.append("audio", audioBlob, "feedback.webm");
      }

      const response = await apiRequest<FeedbackSubmissionResponse>(
        apiEndpoint,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      return response;
    },
    onSuccess: (response) => {
      setIsSubmitted(true);
      reset();
      setAudioBlob(null);
      onSuccess?.(response);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit feedback";
      setError(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    },
  });

  // Handle audio blob from AudioRecorderComponent
  const handleAudioRecorded = useCallback((blob: Blob | null) => {
    setAudioBlob(blob);
  }, []);

  // Handle form reset
  const handleReset = useCallback(() => {
    setIsSubmitted(false);
    reset();
    setAudioBlob(null);
  }, [reset]);

  const Slot = Button || "div";

  const MotionSlot = motion(Slot);

  return (
    <div
      className={cn(
        "voice-feedback-widget fixed bottom-4 right-4 z-50",
        className
      )}
      role="dialog"
      aria-labelledby="feedback-title"
      aria-describedby="feedback-description"
    >
      <motion.div
        initial={false}
        animate={{
          width: isMinimized ? "auto" : 400,
          height: isMinimized ? "auto" : 650,
          borderRadius: isMinimized ? 100 : 12,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
          mass: 0.8,
        }}
        onAnimationStart={() => {
          setIsAnimating(true);
        }}
        onAnimationComplete={() => {
          setIsAnimating(false);
        }}
        className="bg-card backdrop-blur-sm shadow-2xl overflow-hidden border border-border/50"
      >
        <AnimatePresence mode="wait">
          {isMinimized && (
            <motion.div
              key="minimized"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center h-full w-full"
            >
              <button
                type="button"
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:brightness-110 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring/60 active:scale-95 text-base font-semibold text-nowrap"
                aria-label="Open feedback form"
                onClick={() => setIsMinimized(false)}
              >
                <SendIcon className="size-5" />
                Send Feedback
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.3,
              delay: 0.1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="w-full h-full"
          >
            <Card className="flex w-full h-full flex-col gap-0 overflow-hidden border-0 shadow-none">
              <CardHeader className="flex shrink-0 flex-row items-center justify-between pb-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-0.5">
                    <p
                      id="feedback-title"
                      className="text-sm leading-none font-medium"
                    >
                      {title}
                    </p>
                    <div className="flex items-center gap-2">
                      {error ? (
                        <p className="text-destructive text-xs">{error}</p>
                      ) : isSubmitted ? (
                        <div className="flex items-center gap-1 text-xs">
                          <CheckIcon className="size-3" />
                          <span>Feedback submitted!</span>
                        </div>
                      ) : isSubmitting ? (
                        <ShimmeringText
                          text="Submitting feedback"
                          className="text-xs"
                        />
                      ) : (
                        <p
                          id="feedback-description"
                          className="text-muted-foreground text-xs"
                        >
                          {description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex h-2 w-2 rounded-full transition-all duration-300",
                      isSubmitting && "animate-pulse bg-white/40"
                    )}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    onClick={() => setIsMinimized(true)}
                  >
                    <XIcon className="size-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-0">
                {!isAnimating && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-4 p-6 min-h-full"
                  >
                    {isSubmitted ? (
                      <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20">
                          <CheckIcon className="w-8 h-8 text-green-600" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold">Thank you!</h3>
                          <p className="text-sm text-muted-foreground">
                            Your feedback has been submitted successfully.
                          </p>
                        </div>
                        <Button
                          onClick={handleReset}
                          variant="outline"
                          size="sm"
                          aria-label="Submit another feedback"
                        >
                          Submit Another
                        </Button>
                      </div>
                    ) : (
                      <form
                        id="feedback-form"
                        onSubmit={handleSubmit((data) => onSubmit(data))}
                        className="space-y-4"
                      >
                        {/* Name Field */}
                        {showFields.includes("name") && (
                          <div className="space-y-2">
                            <label
                              htmlFor="name"
                              className="text-sm font-medium flex items-center gap-2"
                            >
                              <UserIcon className="size-4" aria-hidden="true" />
                              Name
                            </label>
                            <Input
                              id="name"
                              {...register("name")}
                              placeholder="Your name"
                              className={cn(
                                "focus-visible:ring-0 focus-visible:ring-offset-0",
                                errors.name && "border-destructive"
                              )}
                              disabled={isSubmitting}
                              aria-invalid={!!errors.name}
                              aria-describedby={
                                errors.name ? "name-error" : undefined
                              }
                            />
                            {errors.name && (
                              <p
                                id="name-error"
                                className="text-xs text-destructive"
                                role="alert"
                              >
                                {errors.name.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Email Field */}
                        {showFields.includes("email") && (
                          <div className="space-y-2">
                            <label
                              htmlFor="email"
                              className="text-sm font-medium flex items-center gap-2"
                            >
                              <MailIcon className="size-4" aria-hidden="true" />
                              Email
                            </label>
                            <Input
                              id="email"
                              type="email"
                              {...register("email")}
                              placeholder="your.email@example.com"
                              className={cn(
                                "focus-visible:ring-0 focus-visible:ring-offset-0",
                                errors.email && "border-destructive"
                              )}
                              disabled={isSubmitting}
                              aria-invalid={!!errors.email}
                              aria-describedby={
                                errors.email ? "email-error" : undefined
                              }
                            />
                            {errors.email && (
                              <p
                                id="email-error"
                                className="text-xs text-destructive"
                                role="alert"
                              >
                                {errors.email.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Phone Field */}
                        {showFields.includes("phone") && (
                          <div className="space-y-2">
                            <label
                              htmlFor="phone"
                              className="text-sm font-medium flex items-center gap-2"
                            >
                              <PhoneIcon
                                className="size-4"
                                aria-hidden="true"
                              />
                              Phone
                            </label>
                            <Input
                              id="phone"
                              type="tel"
                              {...register("phone")}
                              placeholder="+1 (555) 123-4567"
                              className={cn(
                                "focus-visible:ring-0 focus-visible:ring-offset-0",
                                errors.phone && "border-destructive"
                              )}
                              disabled={isSubmitting}
                              aria-invalid={!!errors.phone}
                              aria-describedby={
                                errors.phone ? "phone-error" : undefined
                              }
                            />
                            {errors.phone && (
                              <p
                                id="phone-error"
                                className="text-xs text-destructive"
                                role="alert"
                              >
                                {errors.phone.message}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Message Field */}
                        <div className="space-y-2">
                          <label
                            htmlFor="message"
                            className="text-sm font-medium flex items-center gap-2"
                          >
                            <MessageSquareIcon
                              className="size-4"
                              aria-hidden="true"
                            />
                            Feedback
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Please provide feedback either by typing or
                            recording
                          </p>
                          <AudioRecorderComponent
                            onAudioRecorded={handleAudioRecorded}
                            maxRecordingDuration={maxRecordingDuration}
                          />
                          <div className="relative">
                            <Textarea
                              id="message"
                              {...register("message")}
                              placeholder="Optionally, type your feedback here"
                              className={cn(
                                "min-h-24 resize-none focus-visible:ring-0 focus-visible:ring-offset-0",
                                errors.message && "border-destructive"
                              )}
                              disabled={isSubmitting}
                              aria-invalid={!!errors.message}
                              aria-describedby={
                                errors.message ? "message-error" : undefined
                              }
                            />
                          </div>
                          {errors.message && (
                            <p
                              id="message-error"
                              className="text-xs text-destructive"
                              role="alert"
                            >
                              {errors.message.message}
                            </p>
                          )}
                        </div>
                      </form>
                    )}
                  </motion.div>
                )}
              </CardContent>
              <CardFooter className="shrink-0 border-t">
                <div className="flex w-full items-center gap-2">
                  <Button
                    type="submit"
                    form="feedback-form"
                    disabled={!isFormValid || isSubmitting}
                    className="flex-1"
                    aria-label="Submit feedback form"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2Icon
                          className="size-4 animate-spin mr-2"
                          aria-hidden="true"
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <SendIcon className="size-4 mr-2" aria-hidden="true" />
                        Submit Feedback
                      </>
                    )}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
